import React, { useState } from 'react'
import { Grid, Box } from '@material-ui/core'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Security from '@mui/icons-material/Security'
import { Button } from '../../../../components/common/Buttons'
import { useAlertContext } from '../../../../contexts/AlertContext'
import SpacingContainerBase from '../../../../components/layouts/SpacingContainerBase'
import { Paper, SpacingContainer } from '../../../../components/common/Containers'
import Typography from '../../../../components/common/Typography'
import connect from '../connect'

const useStyles = makeStyles(() => ({
  infoText: {
    color: '#1f2933',
    marginLeft: 4,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
    padding: '0.9rem 1rem',
    borderRadius: 10,
    backgroundColor: 'rgba(36, 70, 109, 0.04)',
    border: '1px solid rgba(36, 70, 109, 0.12)',
    width: 'fit-content',
  },
  statusLabel: {
    color: '#111827',
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.75rem',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '0.85rem',
  },
  pillEnabled: {
    backgroundColor: 'rgba(23, 180, 113, 0.12)',
    color: '#0f7a4d',
    border: '1px solid rgba(23, 180, 113, 0.4)',
  },
  pillDisabled: {
    backgroundColor: 'rgba(107, 114, 128, 0.12)',
    color: '#4b5563',
    border: '1px solid rgba(107, 114, 128, 0.35)',
  },
  buttonSection: {
    borderTop: '1px solid rgba(78, 89, 120, 0.12)',
    padding: '2rem 0 0.5rem 0',
    marginTop: '0.5rem',
  },
}))

const CrmAccessStatus = ({ detail, invalidateCrmAccessCache }) => {
  const styles = useStyles()
  const { successAlert, errorAlert } = useAlertContext()
  const [busy, setBusy] = useState(false)

  const crmEnabled = detail?.crmAccessEnabled === true
  const salesforceId =
    detail?.associatedSalesforceId || detail?.salesforce?.associatedSalesforceId

  const handleRefresh = async () => {
    if (!detail?.id || !salesforceId || busy) return

    try {
      setBusy(true)

      // .unwrap() rethrows a rejected thunk so a backend failure hits the catch below,
      // instead of resolving to an action and falsely reporting success.
      const data = await invalidateCrmAccessCache({ organizationId: detail.id }).unwrap()
      const nextEnabled = data?.crmAccessEnabled

      successAlert(
        typeof nextEnabled === 'boolean'
          ? `CRM access cache refreshed from Salesforce: ${nextEnabled ? 'Enabled' : 'Disabled'}.`
          : 'CRM access cache refreshed from Salesforce.'
      )
    } catch {
      errorAlert('Failed to refresh CRM access from Salesforce.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SpacingContainerBase py={0}>
      <Box mt={4}>
        <Paper component={SpacingContainer} py={3} px="5sm3">
          <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
            <Grid item xs zeroMinWidth>
              <Typography
                style={{ paddingBottom: '1rem', alignItems: 'center', display: 'flex' }}
                variant="h4"
                weight="500"
                fontFamily="lexend"
              >
                <Security style={{ marginRight: '0.5rem' }} />
                GovCon CRM Access
              </Typography>
            </Grid>
          </Grid>

          <Box
            style={{
              borderTop: `1px solid rgba(78, 89, 120, 0.12)`,
              padding: '2rem 0 0 0',
            }}
          >
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={12}>
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" className={styles.infoText}>
                    Controlled by the <code>GovConCrmAccess__c</code> field on the
                    Salesforce Account
                    {salesforceId ? ` (${salesforceId})` : ' (no Salesforce ID linked)'}.
                    The platform treats this field as read-only; changes take effect on
                    the user&apos;s next login or session refresh.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box className={styles.statusRow}>
                  <Typography className={styles.statusLabel}>CRM Access</Typography>
                  <span
                    className={`${styles.pill} ${
                      crmEnabled ? styles.pillEnabled : styles.pillDisabled
                    }`}
                  >
                    {crmEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box className={styles.buttonSection}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={busy || !detail?.id || !salesforceId}
                    onClick={handleRefresh}
                  >
                    {busy ? 'Refreshing...' : 'Refresh From Salesforce'}
                  </Button>
                  {!salesforceId && (
                    <Typography
                      variant="body2"
                      className={styles.infoText}
                      style={{ marginTop: '0.75rem' }}
                    >
                      Link a Salesforce Account to this organization to enable refresh.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </SpacingContainerBase>
  )
}

export default connect(CrmAccessStatus)
